import CatalogedProduct from "shared-ui/src/types/ecommerce/CatalogedProduct";
import {
  AddShoppingCartDto,
  EcommerceShoppingCartClient,
  ProductToAdd,
} from "@lib/ShiOneClient";
import { useApi } from "@utils/useApi";
import { useClaims } from "../../../../../auth/ClaimsProvider";
import { ShiOnePermission } from "../../../../../auth/authorizationTypes";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Toast } from "shared-ui/src/components/Toast";
import { invalidateCart } from "@features/ecommerce/cart/cartQueryKeys";

const ProvidingModule = "CX.Product.AddShoppingCart";

export type AddShoppingCartProductInput = CatalogedProduct & {
  quantity?: number;
};

const useAddShoppingCartProduct = () => {
  const queryClient = useQueryClient();
  const api = useApi(EcommerceShoppingCartClient);
  const { hasPermission } = useClaims();

  const addToCart = async (product: AddShoppingCartProductInput) => {
    if (!hasPermission(ShiOnePermission.shoppingCartSave)) {
      throw new Error("Access denied: Add to shopping cart.");
    }
    if (!product.productDescriptor || !product.productClass) {
      throw new Error("Invalid product: Missing Descriptor or Class.");
    }

    const productToAdd = new ProductToAdd({
      productDescriptor: product.productDescriptor,
      productClass: product.productClass,
      quantity: product.quantity ?? 1,
    });

    const dto = new AddShoppingCartDto();
    dto.products = [productToAdd];
    dto.providingModule = ProvidingModule;

    return await api.addShoppingCartItem(dto);
  };

  return useMutation<string, Error, AddShoppingCartProductInput>({
    mutationFn: addToCart,
    onSuccess: async (_count, product) => {
      await invalidateCart(queryClient);
      const quantity = product.quantity ?? 1;
      Toast(`Added ${quantity} ${product.name} to cart`);
    },
    onError: (error, product) => {
      Toast(
        `Failed Add to shopping cart: ${product.name} Reason: ${error?.message}`,
        true,
        7000,
      );
    },
  });
};

export default useAddShoppingCartProduct;
